/**
 * Google Analytics 4 (GA4) utility functions.
 * Provides event tracking for video interactions, filters, pagination, search, and redirects.
 */

// GA4 Measurement ID from environment
export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Check if we're in staging/development for debug mode
const IS_DEBUG = process.env.REACT_APP_STAGING === 'true' || process.env.NODE_ENV === 'development';

// Type declaration for gtag
declare global {
    interface Window {
        gtag?: (...args: any[]) => void;
        dataLayer?: any[];
    }
}

/**
 * Initialize Google Analytics.
 * Should be called once on app load.
 */
export function initGA(): void {
    if (!GA_MEASUREMENT_ID) {
        if (IS_DEBUG) {
            console.log('[Analytics] No GA_MEASUREMENT_ID configured, skipping initialization');
        }
        return;
    }

    // Ensure dataLayer exists
    window.dataLayer = window.dataLayer || [];

    // Fallback: define gtag if it doesn't exist (e.g. if script loading failed or index.html logic changed)
    if (!window.gtag) {
        window.gtag = function (...args: any[]) {
            window.dataLayer?.push(args);
        };
    }

    const gtag = window.gtag;

    // Configure with debug mode enabled for staging
    // Note: 'js', new Date() is now called in index.html for earlier initialization
    gtag('config', GA_MEASUREMENT_ID, {
        debug_mode: IS_DEBUG,
        send_page_view: true,
    });

    if (IS_DEBUG) {
        console.log('[Analytics] GA4 initialized with debug mode:', GA_MEASUREMENT_ID);
    }
}

/**
 * Track a page view.
 */
export function trackPageView(path: string, title?: string): void {
    if (!window.gtag || !GA_MEASUREMENT_ID) return;

    window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title,
    });

    if (IS_DEBUG) {
        console.log('[Analytics] Page view:', path);
    }
}

/**
 * Track a generic event.
 */
export function trackEvent(action: string, params: Record<string, any> = {}): void {
    if (!window.gtag || !GA_MEASUREMENT_ID) return;

    window.gtag('event', action, params);

    if (IS_DEBUG) {
        console.log('[Analytics] Event:', action, params);
    }
}

/**
 * Track video tile click.
 */
export function trackVideoClick(
    videoId: string | number,
    title: string,
    position: number,
    charity?: string
): void {
    trackEvent('video_tile_click', {
        video_id: videoId,
        video_title: title,
        position: position,
        charity_name: charity,
        event_category: 'Video',
    });
}

/**
 * Track filter applied (category, charity, etc.).
 */
export function trackFilterApplied(
    filterType: string,
    filterValue: string | string[],
    resultCount: number
): void {
    trackEvent('filter_applied', {
        filter_type: filterType,
        filter_value: Array.isArray(filterValue) ? filterValue.join(',') : filterValue,
        result_count: resultCount,
        event_category: 'Filter',
    });
}

/**
 * Track pagination interaction.
 */
export function trackPaginationClick(
    pageNumber: number,
    direction: 'next' | 'prev' | 'jump' | 'direct',
    totalPages: number
): void {
    trackEvent('pagination_click', {
        page_number: pageNumber,
        direction: direction,
        total_pages: totalPages,
        event_category: 'Pagination',
    });
}

/**
 * Track search query (call after debounce).
 */
export function trackSearchQuery(query: string, resultCount: number): void {
    if (!query.trim()) return; // Don't track empty searches

    trackEvent('search_query', {
        search_term: query,
        result_count: resultCount,
        event_category: 'Search',
    });
}

/**
 * Track redirect triggered.
 */
export function trackRedirectTriggered(
    sourcePath: string,
    destinationUrl: string,
    statusCode: number
): void {
    trackEvent('redirect_triggered', {
        source_path: sourcePath,
        destination_url: destinationUrl,
        status_code: statusCode,
        event_category: 'Redirect',
    });
}
